import { faPlus, faArrowLeft, faArrowRight, faCheck, faSpinner, faStar, faTruck, faWarehouse } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAppContext } from '../../../context/AppContext';
import addOnsData from '../../../assets/libraries/add-ons.json';
import { postData, trackQuoteRequested } from '../../../services';
import { RenderDesigns } from '../../blocks/Render';
import { isEcoBeamDesign, getEcoBeamPricePerMount } from '../../../utils/ecoBeamPricing';
import { isQuotableDesign } from '../../../utils/quotableDesign';
import { isEmptyAddress } from '../../../utils/projects';
import { useState, useEffect } from 'react';
import Modal from '../../helpers/Modal';
import { LeadTimeNotice } from '../../helpers/LeadTimeNotice';

/** Confirm step: list/MSRP vs Solar Pro (overrides .summary-col-total / .total-line weight) */
const SUMMARY_MSRP_PRICE_STYLE = { color: '#666', fontWeight: 'normal' };
const SOLAR_PRO_LABEL_STYLE = { color: '#111', fontWeight: 'bold' };
const SUMMARY_SOLAR_PRO_PRICE_STYLE = { color: '#638a47', fontWeight: 'bold' };
const DEFAULT_COUPON_CODE = '';
const VALID_PROMO_COUPON_CODES = new Set(['RAISE15', 'NATURALFINISH15']);
const PROMO_COUPON_DISCOUNT_MULTIPLIER = 0.85;
const PRICE_STACK_STYLE = { display: 'flex', flexDirection: 'column', gap: '0.25rem', lineHeight: 1.35 };

export const NewQuoteForm = ({
    project,
    designs,
    setQuotes,
    newQuotePrefill = null,
    onNewQuotePrefillConsumed,
}) => {
    const { token, base_url, user, company, currentLeadTime: leadTime } = useAppContext();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [selectedDesigns, setSelectedDesigns] = useState({}); // { [designId]: quantity }
    const [selectedAddOns, setSelectedAddOns] = useState({}); // { [addOnSku]: quantity }
    const [engineeredDesigns, setEngineeredDesigns] = useState([]); // Array of unique_ids for designs needing engineering
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [freightOptions, setFreightOptions] = useState([]);
    const [selectedFreight, setSelectedFreight] = useState(null); // { type: 'pickup' | 'delivery', serviceType?: string, cost: number }
    const [isLoadingFreight, setIsLoadingFreight] = useState(false);
    const [freightError, setFreightError] = useState(null);
    const [freightMsg, setFreightMsg] = useState(null);
    const [freightFetched, setFreightFetched] = useState(false);
    const [selectedDeliveryOptions, setSelectedDeliveryOptions] = useState({
        residential: false,
        liftGate: false,
        notifyBeforeDelivery: false,
        appointmentRequired: false
    });
    const [couponCode, setCouponCode] = useState(DEFAULT_COUPON_CODE);
    const passedDesigns = designs.filter(
        (design) => design.design_passed === true && isQuotableDesign(design)
    );

    useEffect(() => {
        if (!newQuotePrefill?.designId) return;

        const isProjectInRestrictedStatus =
            project?.current_stage === 'manufacturing' || project?.current_stage === 'shipped';
        const canOpen =
            project?.company_id !== 1 &&
            !isEmptyAddress(project?.shipping_address) &&
            !isProjectInRestrictedStatus;
        const designExists = passedDesigns.some((d) => d.id === newQuotePrefill.designId);

        if (!canOpen || !designExists) {
            onNewQuotePrefillConsumed?.();
            return;
        }

        setCurrentStep(1);
        setSelectedDesigns({ [newQuotePrefill.designId]: 1 });
        setSelectedAddOns({});
        setEngineeredDesigns([]);
        setSelectedFreight(null);
        setFreightOptions([]);
        setFreightError(null);
        setFreightMsg(null);
        setFreightFetched(false);
        setSelectedDeliveryOptions({
            residential: false,
            liftGate: false,
            notifyBeforeDelivery: false,
            appointmentRequired: false,
        });
        setCouponCode(DEFAULT_COUPON_CODE);
        setIsSubmitting(false);
        setIsModalOpen(true);
        onNewQuotePrefillConsumed?.();
    }, [newQuotePrefill, passedDesigns, project, onNewQuotePrefillConsumed]);

    // Delivery options with their costs
    const deliveryOptions = [
        { key: 'residential', label: 'Residential Address', cost: 100 },
        { key: 'liftGate', label: 'Lift Gate Delivery', cost: 75 },
        { key: 'notifyBeforeDelivery', label: 'Notify Before Delivery', cost: 30 },
        { key: 'appointmentRequired', label: 'Delivery Appointment Required', cost: 30 }
    ];

    // Helper function to format numbers with commas
    const formatPrice = (price) => {
        return price ? price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00';
    };

    const canShowDesignPricing = () => {
        if (user?.user_type === 'Internal' || user?.is_super_admin === true) return true;
        if (user?.user_type === 'Standard') {
            const msrpTypes = ['End User / Property Owner', 'Property Owner', 'MSRP', 'PROSPECT'];
            const solarProTypes = [
                'Engineering Firm', 'Installer', 'General Contractor', 'Subcontractor', 'Solar Pro',
                'Installer / Solar Pro', 'Electrical / General Contractor', 'EPC / Developer',
            ];
            return msrpTypes.includes(company?.company_type) || solarProTypes.includes(company?.company_type);
        }
        return false;
    };

    // Calculate pricing based on user_type, company_type, and quantity (eco-beam tiers)
    const getDesignPricing = (design, quantity = 0) => {
        const basePrice = design.total_design_price;
        const mountQty = Math.max(0, Number(quantity) || 0);

        if (design.is_custom) {
            return {
                showPricing: false,
                isEcoBeam: false,
                originalPrice: basePrice,
                discountedPrice: basePrice,
                solarProPrice: null,
                solarProDiscountedPrice: null,
                showDiscount: false,
                showBothPrices: false
            };
        }

        if (isEcoBeamDesign(design)) {
            const unitPrice = getEcoBeamPricePerMount(mountQty);
            return {
                showPricing: canShowDesignPricing(),
                isEcoBeam: true,
                originalPrice: unitPrice,
                discountedPrice: unitPrice,
                solarProPrice: null,
                solarProDiscountedPrice: null,
                showDiscount: false,
                showBothPrices: false,
            };
        }
        
        // Internal staff and super admins see list + Solar Pro (same as design-selection step)
        if (user?.user_type === 'Internal' || user?.is_super_admin === true) {
            return {
                showPricing: true,
                isEcoBeam: false,
                originalPrice: basePrice,
                discountedPrice: basePrice,
                solarProPrice: basePrice * 0.7,
                solarProDiscountedPrice: basePrice * 0.7,
                showDiscount: false,
                showBothPrices: true
            };
        }
        
        if (user?.user_type === 'Standard') {
            if (['End User / Property Owner', 'Property Owner', 'MSRP', 'PROSPECT'].includes(company?.company_type)) {
                return {
                    showPricing: true,
                    isEcoBeam: false,
                    originalPrice: basePrice,
                    discountedPrice: basePrice,
                    solarProPrice: null,
                    solarProDiscountedPrice: null,
                    showDiscount: false,
                    showBothPrices: false
                };
            }
            
            if (['Engineering Firm', 'Installer', 'General Contractor', 'Subcontractor', 'Solar Pro', 'Installer / Solar Pro', 'Electrical / General Contractor', 'EPC / Developer'].includes(company?.company_type)) {
                return {
                    showPricing: true,
                    isEcoBeam: false,
                    originalPrice: basePrice,
                    discountedPrice: basePrice * 0.7,
                    solarProPrice: null, // Don't show Solar Pro price separately for Standard users
                    solarProDiscountedPrice: null,
                    showDiscount: true,
                    showBothPrices: false
                };
            }
        }
        
        // Default case - no pricing shown
        return {
            showPricing: false,
            isEcoBeam: false,
            originalPrice: basePrice,
            discountedPrice: basePrice,
            solarProPrice: null,
            solarProDiscountedPrice: null,
            showDiscount: false,
            showBothPrices: false
        };
    };

    const isPromoCouponActive = () =>
        VALID_PROMO_COUPON_CODES.has(couponCode.trim().toUpperCase());

    const applyPromoCouponDiscount = (price) => {
        if (!isPromoCouponActive() || price == null) return price;
        return price * PROMO_COUPON_DISCOUNT_MULTIPLIER;
    };

    /** Unit price for quote totals (Solar Pro discount, then promo coupon stacked). */
    const getEffectiveDesignUnitPrice = (pricing) => {
        const baseUnit = pricing.showDiscount ? pricing.discountedPrice : pricing.originalPrice;
        return applyPromoCouponDiscount(baseUnit);
    };

    const getEffectiveSolarProUnitPrice = (pricing) => {
        if (pricing.solarProPrice == null) return null;
        return applyPromoCouponDiscount(pricing.solarProPrice);
    };

    const renderDiscountPriceStack = (originalAmount, discountedAmount, align = 'flex-end') => (
        <div style={{ ...PRICE_STACK_STYLE, alignItems: align }}>
            <span style={{ textDecoration: 'line-through', ...SUMMARY_MSRP_PRICE_STYLE }}>
                ${formatPrice(originalAmount)}
            </span>
            <span style={SUMMARY_SOLAR_PRO_PRICE_STYLE}>${formatPrice(discountedAmount)}</span>
        </div>
    );

    const renderDualPricingStack = (listAmount, solarProAmount, effectiveSolarProAmount, align = 'flex-end') => {
        const promoCouponActive = isPromoCouponActive();
        return (
            <div style={{ ...PRICE_STACK_STYLE, alignItems: align }}>
                <span style={SUMMARY_MSRP_PRICE_STYLE}>${formatPrice(listAmount)}</span>
                <span style={SOLAR_PRO_LABEL_STYLE}>Solar Pro:</span>
                {promoCouponActive ? (
                    <>
                        <span style={{ textDecoration: 'line-through', ...SUMMARY_MSRP_PRICE_STYLE }}>
                            ${formatPrice(solarProAmount)}
                        </span>
                        <span style={SUMMARY_SOLAR_PRO_PRICE_STYLE}>
                            ${formatPrice(effectiveSolarProAmount)}
                        </span>
                    </>
                ) : (
                    <span style={SUMMARY_SOLAR_PRO_PRICE_STYLE}>${formatPrice(solarProAmount)}</span>
                )}
            </div>
        );
    };

    const handleDesignQuantityChange = (designId, quantity) => {
        setSelectedDesigns(prev => {
            const newSelected = { ...prev };
            if (quantity > 0) {
                newSelected[designId] = quantity;
            } else {
                delete newSelected[designId];
            }
            return newSelected;
        });
    };

    const handleAddOnQuantityChange = (addOnSku, quantity) => {
        setSelectedAddOns(prev => {
            const newSelected = { ...prev };
            if (quantity > 0) {
                newSelected[addOnSku] = quantity;
            } else {
                delete newSelected[addOnSku];
            }
            return newSelected;
        });
    };

    const handleEngineeringToggle = (designUniqueId) => {
        setEngineeredDesigns(prev => {
            if (prev.includes(designUniqueId)) {
                return prev.filter(id => id !== designUniqueId);
            } else {
                return [...prev, designUniqueId];
            }
        });
    };

    const getSelectedDesignsData = () => {
        return passedDesigns
            .filter(design => selectedDesigns[design.id] > 0)
            .map(design => ({ ...design, quantity: selectedDesigns[design.id] }));
    };

    const getSelectedAddOnsData = () => {
        const allAddOns = [
            ...addOnsData.installation, 
            ...addOnsData.accessories,
        ];
        
        return allAddOns
            .filter(addOn => selectedAddOns[addOn.sku] > 0)
            .map(addOn => ({ ...addOn, quantity: selectedAddOns[addOn.sku] }));
    };

    const calculateTotal = () => {
        const designsTotal = getSelectedDesignsData().reduce((sum, design) => {
            if (design.is_custom) return sum;
            const pricing = getDesignPricing(design, design.quantity);
            return sum + getEffectiveDesignUnitPrice(pricing) * design.quantity;
        }, 0);
        const addOnsTotal = getSelectedAddOnsData().reduce((sum, addOn) => sum + (addOn.price * addOn.quantity), 0);
        const freightCost = selectedFreight?.cost || 0;
        return designsTotal + addOnsTotal + freightCost;
    };

    /** MSRP subtotal for selected designs (excludes custom / non-priced rows). */
    const calculateDesignsMsrpSubtotal = () => {
        return getSelectedDesignsData().reduce((sum, design) => {
            if (design.is_custom) return sum;
            const pricing = getDesignPricing(design, design.quantity);
            if (!pricing.showPricing) return sum;
            return sum + pricing.originalPrice * design.quantity;
        }, 0);
    };

    /** Solar Pro subtotal for dual-pricing viewers (Internal / super admin). */
    const calculateDesignsSolarProSubtotal = () => {
        return getSelectedDesignsData().reduce((sum, design) => {
            if (design.is_custom) return sum;
            const pricing = getDesignPricing(design, design.quantity);
            const solarProUnit = getEffectiveSolarProUnitPrice(pricing);
            if (!pricing.showBothPrices || solarProUnit == null) return sum;
            return sum + solarProUnit * design.quantity;
        }, 0);
    };

    // Calculate total delivery options surcharge
    const calculateDeliveryOptionsSurcharge = () => {
        return deliveryOptions.reduce((total, option) => {
            if (selectedDeliveryOptions[option.key]) {
                return total + option.cost;
            }
            return total;
        }, 0);
    };

    // Fetch freight quotes when user clicks "Get Rates"
    const fetchFreightQuotes = async () => {
        setIsLoadingFreight(true);
        setFreightError(null);
        setFreightMsg(null);
        setSelectedFreight(null); // Reset selection when fetching new rates
        
        try {
            const selectedDesignsData = getSelectedDesignsData();
            const selectedAddOnsData = getSelectedAddOnsData();
            
            // Calculate total weight of all add-ons (weight * quantity)
            const totalAddOnsWeight = selectedAddOnsData.reduce((sum, addOn) => {
                return sum + ((addOn.weight || 0) * addOn.quantity);
            }, 0);
            
            // Add add-ons weight to the first design's total_design_weight for freight calculation
            if (selectedDesignsData.length > 0 && totalAddOnsWeight > 0) {
                selectedDesignsData[0] = {
                    ...selectedDesignsData[0],
                    total_design_weight: (selectedDesignsData[0].total_design_weight || 0) + totalAddOnsWeight
                };
            }
            
            // Filter delivery options to only include selected ones
            const filteredDeliveryOptions = {};
            Object.keys(selectedDeliveryOptions).forEach(key => {
                if (selectedDeliveryOptions[key]) {
                    filteredDeliveryOptions[key] = true;
                }
            });
            
            const freightData = {
                project_site_id: project.id,
                designs: selectedDesignsData,
                deliveryOptions: filteredDeliveryOptions
            };
            
            const response = await postData(token, `${base_url}/apis/billing/get-freight-quote`, freightData);
            
            if (response.quotes && response.quotes.length > 0) {
                setFreightOptions(response.quotes);
            } else {
                setFreightOptions([]);
                if (response.msg) {
                    setFreightMsg(response.msg);
                }
                setSelectedFreight({ type: 'pickup', serviceType: null, cost: 0, deliveryOptions: null });
            }
            setFreightFetched(true);
        } catch (error) {
            console.error('Error fetching freight quotes:', error);
            setFreightError('Unable to fetch freight quotes. You can still select "Pickup from Shop" or try again.');
            setFreightOptions([]);
            setFreightFetched(true);
        } finally {
            setIsLoadingFreight(false);
        }
    };

    const handleDeliveryOptionChange = (optionKey) => {
        setSelectedDeliveryOptions(prev => ({
            ...prev,
            [optionKey]: !prev[optionKey]
        }));
        // Reset freight options when delivery options change so user needs to re-fetch
        setFreightFetched(false);
        setFreightOptions([]);
        setFreightMsg(null);
        setSelectedFreight(null);
    };

    const formatServiceType = (serviceType) => {
        // Convert FEDEX_FREIGHT_PRIORITY to "FedEx Freight Priority"
        return serviceType
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    const handleSubmit = async () => {
        if (isSubmitting) return; // Prevent multiple submissions
        
        setIsSubmitting(true);
        
        try {
            const quoteData = {
                project_id: project.id,
                designs: getSelectedDesignsData(),
                addOns: getSelectedAddOnsData(),
                lead_time: leadTime,
                engineered_designs: engineeredDesigns,
                freight: {
                    type: selectedFreight?.type || 'pickup',
                    serviceType: selectedFreight?.serviceType || null,
                    cost: selectedFreight?.cost || 0,
                    deliveryOptions: selectedFreight?.deliveryOptions || null
                },
                coupon_code: couponCode.trim(),
            }

            console.log('Creating new quote:');
            const new_quote = await postData(token, `${base_url}/apis/billing`, quoteData);

            if (new_quote?.quote) {
                setQuotes((prevQuotes) => [new_quote.quote, ...prevQuotes]);
                const selectedDesignsData = getSelectedDesignsData();
                trackQuoteRequested({
                    companyId: project?.company_id || company?.id || user?.company_id,
                    projectId: project?.id,
                    designId: selectedDesignsData[0]?.unique_id,
                    quote: new_quote.quote,
                    value: new_quote.quote.total_amount ?? calculateTotal(),
                });
            }

            // If freight returned a message (no LTL rates available), auto-submit a freight support request
            if (freightMsg) {
                try {
                    await postData(token, `${base_url}/apis/alerts/support-request`, {
                        project_id: project?.id,
                        project_name: project?.project_name || project?.name || '',
                        severity: 'high',
                        description: 'The customer has requested a freight quote for this project. Please provide a freight shipping estimate at your earliest convenience.',
                        first_name: user?.first_name || '',
                        last_name: user?.last_name || '',
                        email: user?.email || '',
                        freight_support: true
                    });
                } catch (freightErr) {
                    console.error('Error submitting freight support request:', freightErr);
                }
            }

            if (window.showAlert) {
                window.showAlert(200, "Your quote request has been submitted successfully! You will receive a quote via email shortly.");
            }

            setIsModalOpen(false);
            // Reset form
            setCurrentStep(1);
            setSelectedDesigns({});
            setSelectedAddOns({});
            setEngineeredDesigns([]);
            setSelectedFreight(null);
            setFreightOptions([]);
            setFreightMsg(null);
            setFreightFetched(false);
            setSelectedDeliveryOptions({
                residential: false,
                liftGate: false,
                notifyBeforeDelivery: false,
                appointmentRequired: false
            });
            setCouponCode(DEFAULT_COUPON_CODE);
        } catch (error) {
            console.error('Error submitting quote:', error);
            if (window.showAlert) {
                window.showAlert(500, "There was an error submitting your quote request. Please try again.");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const nextStep = () => {
        if (currentStep < 5) {
            setCurrentStep(currentStep + 1);
        }
    };

    const prevStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setCurrentStep(1);
        setSelectedDesigns({});
        setSelectedAddOns({});
        setEngineeredDesigns([]);
        setSelectedFreight(null);
        setFreightOptions([]);
        setFreightError(null);
        setFreightMsg(null);
        setFreightFetched(false);
        setSelectedDeliveryOptions({
            residential: false,
            liftGate: false,
            notifyBeforeDelivery: false,
            appointmentRequired: false
        });
        setCouponCode(DEFAULT_COUPON_CODE);
        setIsSubmitting(false); // Reset loading state when modal is closed
    }

    const renderStepIndicator = () => (
        <div className="step-indicator">
            <div className={`step ${currentStep >= 1 ? 'active' : ''}`}>
                <span className="quote-step-number">1</span>
                <span className="step-label">Designs</span>
            </div>
            <div className={`step ${currentStep >= 2 ? 'active' : ''}`}>
                <span className="quote-step-number">2</span>
                <span className="step-label">Installation Tools</span>
            </div>
            <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
                <span className="quote-step-number">3</span>
                <span className="step-label">Accessories</span>
            </div>
            <div className={`step ${currentStep >= 4 ? 'active' : ''}`}>
                <span className="quote-step-number">4</span>
                <span className="step-label">Freight</span>
            </div>
            <div className={`step ${currentStep >= 5 ? 'active' : ''}`}>
                <span className="quote-step-number">5</span>
                <span className="step-label">Confirm</span>
            </div>
        </div>
    );

    const renderDesignsStep = () => (
        <div className="quote-step">
            <h2>Select Your Designs</h2>
            <p>Choose the designs you'd like to include in your quote (enter quantity):</p>
            
            <div className="designs-list">
                {[...passedDesigns]
                    .sort((a, b) => {
                        const aSelected = (selectedDesigns[a.id] || 0) > 0 ? 1 : 0;
                        const bSelected = (selectedDesigns[b.id] || 0) > 0 ? 1 : 0;
                        if (aSelected !== bSelected) return bSelected - aSelected;
                        const fav = (b.is_favorited ? 1 : 0) - (a.is_favorited ? 1 : 0);
                        if (fav !== 0) return fav;
                        const dateA = new Date(a.created_at || 0).getTime();
                        const dateB = new Date(b.created_at || 0).getTime();
                        return dateB - dateA;
                    })
                    .map(design => {
                        const designQty = selectedDesigns[design.id] || 0;
                        const pricing = getDesignPricing(design, designQty);
                        const effectiveUnit = getEffectiveDesignUnitPrice(pricing);
                        const effectiveSolarPro = getEffectiveSolarProUnitPrice(pricing);
                        const promoCouponActive = isPromoCouponActive();
                        return (
                            <div key={design.id} className="design-item" style={{ flexWrap: 'wrap' }}>
                            <label className="quantity-label">
                                <div className="design-info">
                                    <span className="quote-design-name">
                                        {design.design_name}
                                        {design.is_favorited && (
                                            <FontAwesomeIcon 
                                                icon={faStar} 
                                                style={{ color: '#f2c54b', marginLeft: '6px', fontSize: '0.85em' }} 
                                                title="Favorited design"
                                            />
                                        )}
                                    </span>
                                    <span className="design-sku">{design.unique_id}</span>
                                </div>
                                <div className="design-pricing-actions">
                                    {pricing.showPricing ? (
                                        <div className="design-pricing">
                                            {pricing.showBothPrices && pricing.solarProPrice != null ? (
                                                renderDualPricingStack(
                                                    pricing.originalPrice,
                                                    pricing.solarProPrice,
                                                    effectiveSolarPro
                                                )
                                            ) : pricing.showDiscount || (promoCouponActive && effectiveUnit < pricing.originalPrice) ? (
                                                renderDiscountPriceStack(pricing.originalPrice, effectiveUnit)
                                            ) : (
                                                <span className="design-price">${formatPrice(pricing.originalPrice)}</span>
                                            )}
                                        </div>
                                    ) : (
                                        <span className="design-price">Contact for pricing</span>
                                    )}
                                    <RenderDesigns design={design} />
                                </div>
                            <div className="quantity-pill-selector">
                                <input
                                    type="number"
                                    className="pill-qty-input"
                                    value={selectedDesigns[design.id] || 0}
                                    onChange={(e) => {
                                        const value = parseInt(e.target.value) || 0;
                                        handleDesignQuantityChange(design.id, Math.max(0, value));
                                    }}
                                    min="0"
                                />
                            </div>
                        </label>
                        <div style={{ flexBasis: '100%', marginTop: '0.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                            <label 
                                style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '0.5rem', 
                                    cursor: 'pointer',
                                    fontSize: '0.9rem'
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={engineeredDesigns.includes(design.unique_id)}
                                    onChange={() => handleEngineeringToggle(design.unique_id)}
                                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                />
                                <span>Order engineering for this design</span>
                            </label>
                        </div>
                            </div>
                        );
                    })}
            </div>
            
            {passedDesigns.length === 0 && (
                <p className="no-items">No designs available for selection.</p>
            )}
        </div>
    );

    const renderInstallationToolsStep = () => (
        <div className="quote-step">
            <h2>Select Installation Tools</h2>
            <p>Choose any installation tools you'd like to include (enter quantity):</p>
            
            <div className="addons-list">
                {addOnsData.installation.map(addOn => (
                    <div key={addOn.sku} className="addon-item">
                        <label className="quantity-label">
                            <div className="addon-info">
                                <span className="addon-name">{addOn.name}</span>
                                <small style={{ whiteSpace: 'pre-line' }}>{addOn.description}</small>
                                <hr />
                                <span className="addon-price">${addOn.price.toFixed(2)}</span>
                            </div>
                            {/* <button className="open-icon-btn" title="Open details">
                                <FontAwesomeIcon icon={faExternalLinkAlt} width={32} height={32} />
                            </button> */}
                            <div className="quantity-pill-selector">
                                <input
                                    type="number"
                                    className="pill-qty-input"
                                    value={selectedAddOns[addOn.sku] || 0}
                                    onChange={(e) => {
                                        const value = parseInt(e.target.value) || 0;
                                        handleAddOnQuantityChange(addOn.sku, Math.max(0, value));
                                    }}
                                    min="0"
                                />
                            </div>
                        </label>
                    </div>
                ))}
            </div>
            
            {addOnsData.installation.length === 0 && (
                <p className="no-items">No installation tools available for selection.</p>
            )}
        </div>
    );


    const renderAccessoriesStep = () => (
        <div className="quote-step">
            <h2>Select Accessories</h2>
            <p>Choose any accessories you'd like to include (enter quantity):</p>
            
            <div className="addons-list">
                {addOnsData.accessories.map(addOn => (
                    <div key={addOn.sku} className="addon-item">
                        <label className="quantity-label">
                            <div className="addon-info">
                                <span className="addon-name">{addOn.name}</span>
                                <small style={{ whiteSpace: 'pre-line' }}>{addOn.description}</small>
                                <hr />
                                <span className="addon-price">${addOn.price.toFixed(2)}</span>
                            </div>
                            {/* <button className="open-icon-btn" title="Open details">
                                <FontAwesomeIcon icon={faExternalLinkAlt} width={32} height={32} />
                            </button> */}
                            <div className="quantity-pill-selector">
                                <input
                                    type="number"
                                    className="pill-qty-input"
                                    value={selectedAddOns[addOn.sku] || 0}
                                    onChange={(e) => {
                                        const value = parseInt(e.target.value) || 0;
                                        handleAddOnQuantityChange(addOn.sku, Math.max(0, value));
                                    }}
                                    min="0"
                                />
                            </div>
                        </label>
                    </div>
                ))}
            </div>
            
            {addOnsData.accessories.length === 0 && (
                <p className="no-items">No accessories available for selection.</p>
            )}
        </div>
    );

    const renderFreightStep = () => {
        const deliveryOptionsSurcharge = calculateDeliveryOptionsSurcharge();
        
        const getFreightPrice = (quote) => {
            const netCharge = quote.ratedShipmentDetails?.[0]?.totalNetCharge || 0;
            return (netCharge * 1.2) + deliveryOptionsSurcharge; // Add 15% markup + delivery options
        };

        const getTransitInfo = (quote) => {
            return quote.commit?.transitDays?.description || 'Transit time varies';
        };

        return (
            <div className="quote-step">
                <h2>Select Shipping Option</h2>
                <p>Choose how you'd like your order shipped:</p>
                
                {/* Shipping Disclaimer */}
                <div style={{
                    backgroundColor: '#fff8e6',
                    border: '1px solid #f0d78c',
                    borderRadius: '8px',
                    padding: '0.75rem 1rem',
                    marginBottom: '1.5rem',
                    fontSize: '0.9rem',
                    color: '#6b5a00'
                }}>
                    <strong>Please Note:</strong> Shipping times are estimates, and not guarantees. We are not responsible for carrier delays, damage during transit, or lost packages once shipped, but please contact us for help if something goes wrong.
                </div>

                {/* Pickup from Shop Option - Always available */}
                <div 
                    className={`freight-option ${selectedFreight?.type === 'pickup' ? 'selected' : ''}`}
                    onClick={() => setSelectedFreight({ type: 'pickup', serviceType: null, cost: 0, deliveryOptions: null })}
                    style={{
                        border: selectedFreight?.type === 'pickup' ? '2px solid #0074a9' : '1px solid #ddd',
                        borderRadius: '8px',
                        padding: '1rem',
                        cursor: 'pointer',
                        backgroundColor: selectedFreight?.type === 'pickup' ? '#f0f7ff' : '#fff',
                        transition: 'all 0.2s ease',
                        marginBottom: '1.5rem'
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <FontAwesomeIcon icon={freightMsg ? faTruck : faWarehouse} size="lg" style={{ color: '#666' }} />
                        <div style={{ flex: 1 }}>
                            <strong style={{ fontSize: '1.1rem' }}>{freightMsg ? 'Truckload Freight' : 'Pickup from Shop'}</strong>
                            <p style={{ margin: '0.25rem 0 0', color: '#666', fontSize: '0.9rem' }}>
                                {freightMsg
                                    ? 'Your shipment requires truckload freight. Our team will contact you with a shipping estimate.'
                                    : 'Pick up your order at our facility in Charlo, MT'}
                            </p>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <span style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#638a47' }}>$0.00</span>
                        </div>
                    </div>
                </div>

                {/* Delivery Options Section — hidden when fetched with no results */}
                {!(freightFetched && freightOptions.length === 0 && !freightError) && (
                    <div style={{ 
                        border: '1px solid #ddd', 
                        borderRadius: '8px', 
                        padding: '1rem',
                        backgroundColor: '#f9f9f9'
                    }}>
                        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem' }}>
                            <FontAwesomeIcon icon={faTruck} style={{ marginRight: '0.5rem', color: '#666' }} />
                            Freight Options
                        </h3>
                        <p style={{ margin: '0 0 1rem 0', color: '#666', fontSize: '0.9rem' }}>
                            Select any additional freight requirements, then click "Get Rates" to see freight options:
                        </p>
                        
                        {/* Delivery Options Checkboxes */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
                            {deliveryOptions.map(option => (
                                <label 
                                    key={option.key}
                                    style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '0.75rem',
                                        cursor: 'pointer',
                                        padding: '0.5rem',
                                        borderRadius: '4px',
                                        backgroundColor: selectedDeliveryOptions[option.key] ? '#e7f3ff' : 'transparent',
                                        transition: 'background-color 0.2s ease'
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedDeliveryOptions[option.key]}
                                        onChange={() => handleDeliveryOptionChange(option.key)}
                                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                    />
                                    <span>{option.label}</span>
                                </label>
                            ))}
                        </div>

                        {/* Get Rates Button */}
                        <button
                            className="btn btn-primary"
                            onClick={fetchFreightQuotes}
                            disabled={isLoadingFreight}
                            style={{ width: '100%', padding: '0.75rem' }}
                        >
                            {isLoadingFreight ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.5rem' }} />
                                    Loading Rates...
                                </>
                            ) : (
                                freightFetched ? 'Refresh Rates' : 'Get Rates'
                            )}
                        </button>

                        {/* Freight Options List */}
                        {isLoadingFreight ? (
                            <div style={{ textAlign: 'center', padding: '2rem' }}>
                                <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                                <p style={{ marginTop: '1rem' }}>Loading freight options...</p>
                            </div>
                        ) : freightFetched && freightOptions.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
                                {freightOptions.map((quote, index) => {
                                    const price = getFreightPrice(quote);
                                    const isSelected = selectedFreight?.type === 'delivery' && selectedFreight?.serviceType === quote.serviceType;
                                    
                                    return (
                                        <div 
                                            key={quote.serviceType || index}
                                            className={`freight-option ${isSelected ? 'selected' : ''}`}
                                            onClick={() => setSelectedFreight({ 
                                                type: 'delivery', 
                                                serviceType: quote.serviceType, 
                                                cost: price,
                                                deliveryOptions: { ...selectedDeliveryOptions }
                                            })}
                                            style={{
                                                border: isSelected ? '2px solid #0074a9' : '1px solid #ddd',
                                                borderRadius: '8px',
                                                padding: '1rem',
                                                cursor: 'pointer',
                                                backgroundColor: isSelected ? '#f0f7ff' : '#fff',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                                <FontAwesomeIcon icon={faTruck} size="lg" style={{ color: '#666' }} />
                                                <div style={{ flex: 1 }}>
                                                    <strong style={{ fontSize: '1.1rem' }}>{formatServiceType(quote.serviceType)}</strong>
                                                    <p style={{ margin: '0.25rem 0 0', color: '#666', fontSize: '0.9rem' }}>
                                                        {getTransitInfo(quote)}
                                                    </p>
                                                </div>
                                                <div style={{ textAlign: 'right' }}>
                                                    <span style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>${formatPrice(price)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {freightError && (
                            <div style={{ 
                                marginTop: '1rem',
                                padding: '1rem', 
                                backgroundColor: '#fff3cd', 
                                border: '1px solid #ffc107', 
                                borderRadius: '8px',
                                color: '#856404'
                            }}>
                                <p style={{ margin: 0 }}>{freightError}. Please select "Pickup from Shop" above or contact us for assistance.</p>
                            </div>
                        )}
                    </div>
                )}

                {/* No freight quotes available — defaulted to pickup */}
                {freightFetched && freightOptions.length === 0 && !freightError && (
                    <div style={{ textAlign: 'center', padding: '1rem' }}>
                        <p style={{ color: '#666', fontStyle: 'italic', margin: '0 0 1rem 0' }}>
                            {freightMsg || 'No freight delivery options are available for this location. Your order has been defaulted to pickup.'}
                        </p>
                    </div>
                )}
            </div>
        );
    };

    const renderSummaryStep = () => {
        const selectedDesignsData = getSelectedDesignsData();
        const selectedAddOnsData = getSelectedAddOnsData();
        const total = calculateTotal();
        const addOnsTotal = selectedAddOnsData.reduce((sum, addOn) => sum + (addOn.price * addOn.quantity), 0);
        const freightCost = selectedFreight?.cost || 0;
        const productsSubtotal = total - freightCost;

        const promoCouponActive = isPromoCouponActive();
        const hasDiscountedDesigns = selectedDesignsData.some(d => {
            if (d.is_custom) return false;
            const p = getDesignPricing(d, d.quantity);
            if (p.showDiscount) return true;
            return promoCouponActive && p.showPricing && getEffectiveDesignUnitPrice(p) < p.originalPrice;
        });
        const designsMsrpSubtotal = calculateDesignsMsrpSubtotal();
        const designsSolarProSubtotal = calculateDesignsSolarProSubtotal();
        const showDualProductTotals = selectedDesignsData.some(d => {
            if (d.is_custom) return false;
            const p = getDesignPricing(d, d.quantity);
            return p.showBothPrices && p.showPricing;
        });

        return (
            <div className="quote-step">
                <h2>Confirm Your Quote</h2>
                <p>Review your selections before submitting:</p>
                
                <div className="summary-section">
                    <h3>Selected Designs ({selectedDesignsData.length})</h3>
                    {selectedDesignsData.length > 0 ? (
                        <div className="summary-table">
                            <div className="summary-row summary-header">
                                <span className="summary-col-name">Name</span>
                                <span className="summary-col-qty">Qty</span>
                                <span className="summary-col-unit">Unit Price</span>
                                <span className="summary-col-total">Line Total</span>
                            </div>
                            {selectedDesignsData.map(design => {
                                const pricing = getDesignPricing(design, design.quantity);
                                const unitPrice = getEffectiveDesignUnitPrice(pricing);
                                const lineTotal = unitPrice * design.quantity;
                                const lineMsrp = pricing.originalPrice * design.quantity;
                                const effectiveSolarProUnit = getEffectiveSolarProUnitPrice(pricing);
                                const lineSolarPro = effectiveSolarProUnit != null
                                    ? effectiveSolarProUnit * design.quantity
                                    : null;
                                
                                return (
                                    <div
                                        key={design.id}
                                        className="summary-row"
                                        style={{ alignItems: 'flex-start', paddingTop: '0.5rem', paddingBottom: '0.5rem' }}
                                    >
                                        <span className="summary-col-name">{design.design_name}</span>
                                        <span className="summary-col-qty">{design.quantity}</span>
                                        <span className="summary-col-unit">
                                            {pricing.showPricing ? (
                                                pricing.showBothPrices && pricing.solarProPrice != null ? (
                                                    renderDualPricingStack(
                                                        pricing.originalPrice,
                                                        pricing.solarProPrice,
                                                        effectiveSolarProUnit,
                                                        'flex-end'
                                                    )
                                                ) : pricing.showDiscount || (promoCouponActive && unitPrice < pricing.originalPrice) ? (
                                                    renderDiscountPriceStack(pricing.originalPrice, unitPrice, 'flex-end')
                                                ) : (
                                                    <span className="design-price">${formatPrice(pricing.originalPrice)}</span>
                                                )
                                            ) : (
                                                'Contact for pricing'
                                            )}
                                        </span>
                                        <span className="summary-col-total">
                                            {pricing.showPricing ? (
                                                pricing.showBothPrices && lineSolarPro != null ? (
                                                    renderDualPricingStack(
                                                        lineMsrp,
                                                        pricing.solarProPrice * design.quantity,
                                                        lineSolarPro
                                                    )
                                                ) : pricing.showDiscount || (promoCouponActive && unitPrice < pricing.originalPrice) ? (
                                                    renderDiscountPriceStack(lineMsrp, lineTotal)
                                                ) : (
                                                    `$${formatPrice(lineTotal)}`
                                                )
                                            ) : (
                                                '—'
                                            )}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <p className="no-items">No designs selected</p>
                    )}
                </div>
                
                <div className="summary-section">
                    <h3>Selected Add-ons ({selectedAddOnsData.length})</h3>
                    {selectedAddOnsData.length > 0 ? (
                        <div className="summary-table">
                            <div className="summary-row summary-header">
                                <span className="summary-col-name">Name</span>
                                <span className="summary-col-qty">Qty</span>
                                <span className="summary-col-unit">Unit Price</span>
                                <span className="summary-col-total">Line Total</span>
                            </div>
                            {selectedAddOnsData.map(addOn => (
                                <div key={addOn.sku} className="summary-row">
                                    <span className="summary-col-name">{addOn.name}</span>
                                    <span className="summary-col-qty">{addOn.quantity}</span>
                                    <span className="summary-col-unit">${addOn.price.toFixed(2)}</span>
                                    <span className="summary-col-total">${(addOn.price * addOn.quantity).toFixed(2)}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="no-items">No add-ons selected</p>
                    )}
                </div>
                
                <div className="summary-section">
                    <h3>Shipping</h3>
                    {selectedFreight ? (
                        <div className="summary-table">
                            <div className="summary-row">
                                <span className="summary-col-name">
                                    {selectedFreight.type === 'pickup'
                                        ? (freightMsg ? 'Truckload Freight' : 'Pickup from Shop')
                                        : formatServiceType(selectedFreight.serviceType)}
                                </span>
                                <span className="summary-col-total">${formatPrice(selectedFreight.cost)}</span>
                            </div>
                            {selectedFreight.type === 'delivery' && selectedFreight.deliveryOptions && (
                                <div style={{ marginTop: '0.5rem', paddingLeft: '1rem', fontSize: '0.9rem', color: '#666' }}>
                                    {deliveryOptions
                                        .filter(opt => selectedFreight.deliveryOptions[opt.key])
                                        .map(opt => (
                                            <div key={opt.key}>
                                                <span>• {opt.label}</span>
                                            </div>
                                        ))
                                    }
                                </div>
                            )}
                        </div>
                    ) : (
                        <p className="no-items">No shipping option selected</p>
                    )}
                </div>

                <div className="summary-section">
                    <div className="form-group">
                        <label htmlFor="coupon_code" className="form-label">
                            Coupon Code
                        </label>
                        <input
                            id="coupon_code"
                            type="text"
                            className="form-input"
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value)}
                            placeholder="Enter coupon code (optional)"
                            autoComplete="off"
                        />
                    </div>
                </div>

                <div className="total-section">
                    {showDualProductTotals ? (
                        <>
                            <div className="total-line">
                                <span className="total-label" style={{ fontSize: '1.1rem', fontWeight: 'normal' }}>
                                    Total (list estimate):
                                </span>
                                <span
                                    className="total-amount"
                                    style={{ fontSize: '1.1rem', ...SUMMARY_MSRP_PRICE_STYLE }}
                                >
                                    ${formatPrice(designsMsrpSubtotal + addOnsTotal + freightCost)}
                                </span>
                            </div>
                            <div className="total-line" style={{ marginTop: '0.35rem' }}>
                                <span
                                    className="total-label"
                                    style={{ fontSize: '1.1rem', ...SOLAR_PRO_LABEL_STYLE }}
                                >
                                    Total (Solar Pro estimate):
                                </span>
                                <span
                                    className="total-amount"
                                    style={{ fontSize: '1.1rem', ...SUMMARY_SOLAR_PRO_PRICE_STYLE }}
                                >
                                    ${formatPrice(designsSolarProSubtotal + addOnsTotal + freightCost)}
                                </span>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="total-line">
                                <span className="total-label">Subtotal (Products):</span>
                                <span className="total-amount">
                                    {hasDiscountedDesigns ? (
                                        <span>
                                            <span
                                                style={{
                                                    textDecoration: 'line-through',
                                                    marginRight: '8px',
                                                    ...SUMMARY_MSRP_PRICE_STYLE,
                                                }}
                                            >
                                                ${formatPrice(designsMsrpSubtotal + addOnsTotal)}
                                            </span>
                                            <span style={SUMMARY_SOLAR_PRO_PRICE_STYLE}>
                                                ${formatPrice(productsSubtotal)}
                                            </span>
                                        </span>
                                    ) : (
                                        `$${formatPrice(productsSubtotal)}`
                                    )}
                                </span>
                            </div>
                            <div className="total-line">
                                <span className="total-label">Shipping:</span>
                                <span className="total-amount">${formatPrice(freightCost)}</span>
                            </div>
                            <div className="total-line" style={{ borderTop: '2px solid #333', paddingTop: '0.5rem', marginTop: '0.5rem' }}>
                                <span className="total-label" style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Total Estimated Cost:</span>
                                <span
                                    className="total-amount"
                                    style={
                                        hasDiscountedDesigns
                                            ? { fontSize: '1.1rem', ...SUMMARY_SOLAR_PRO_PRICE_STYLE }
                                            : { fontWeight: 'bold', fontSize: '1.1rem' }
                                    }
                                >
                                    ${formatPrice(total)}
                                </span>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    };

    const renderCurrentStep = () => {
        switch (currentStep) {
            case 1:
                return renderDesignsStep();
            case 2:
                return renderInstallationToolsStep();
            case 3:
                return renderAccessoriesStep();
            case 4:
                return renderFreightStep();
            case 5:
                return renderSummaryStep();
            default:
                return renderDesignsStep();
        }
    };

    const renderNavigation = () => {
        // Disable next button on step 1 if no designs selected
        const hasNoDesignsSelected = currentStep === 1 && Object.keys(selectedDesigns).length === 0;
        // Disable next button on freight step if no freight option selected
        const hasNoFreightSelected = currentStep === 4 && !selectedFreight;
        const isNextDisabled = hasNoDesignsSelected || hasNoFreightSelected;
        
        const getDisabledTitle = () => {
            if (hasNoDesignsSelected) return 'Please select at least one design';
            if (hasNoFreightSelected) return 'Please select a shipping option';
            return '';
        };
        
        return (
            <div className="form-navigation" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                {currentStep > 1 && (
                    <button 
                        className="btn btn-secondary" 
                        onClick={prevStep}
                    >
                        <FontAwesomeIcon icon={faArrowLeft} /> Previous
                    </button>
                )}
                
                {currentStep < 5 ? (
                    <button 
                        className="btn btn-primary" 
                        onClick={nextStep}
                        disabled={isNextDisabled}
                        title={getDisabledTitle()}
                        style={isNextDisabled ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                    >
                        Next <FontAwesomeIcon icon={faArrowRight} />
                    </button>
                ) : (
                    <button 
                        className="btn btn-success" 
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin /> Submitting...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faCheck} /> Submit Quote Request
                            </>
                        )}
                    </button>
                )}
            </div>
        );
    };

    const quoteForm = (
        <div className="quote-form">
            {renderStepIndicator()}
            <LeadTimeNotice designs={designs} />
            {renderCurrentStep()}
            {renderNavigation()}
        </div>
    );

    // Check if project is in manufacturing or shipped status
    const isProjectInRestrictedStatus = project?.current_stage === 'manufacturing' || project?.current_stage === 'shipped';
    const buttonText = isProjectInRestrictedStatus ? 'Quote Not Available' : 'Get a Quote';
    const buttonTitle = isProjectInRestrictedStatus ? 'Quotes are not available for projects in manufacturing or shipped status' : 'Request a quote for this project';

    return (
        <div>
            <button 
                className="btn btn-action btn-primary" 
                onClick={() => setIsModalOpen(true)}
                disabled={isProjectInRestrictedStatus}
                title={buttonTitle}
                style={isProjectInRestrictedStatus ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
            >
                <FontAwesomeIcon icon={faPlus} /> {buttonText}
            </button>
            {isModalOpen && (
                <Modal form={quoteForm} setIsModalOpen={closeModal} isWide={true} />
            )}
        </div>
    );
};
